/**
 * Centralized design tokens.
 * Single source of truth for spacing, shape, elevation, colors.
 */

import type { ThemeVariant } from "@/constants/theme-variant";

/** Spacing scale (px). MUI spacing unit = 8px; use multiples for consistency. */
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  /** 24px */
  xxl: 24,
  /** 32px */
  xxxl: 32,
} as const;

/** Border radius scale (px). */
export const borderRadius = {
  sm: 8,
  md: 14,
  lg: 18,
  xl: 22,
  /** Modal container - reduced radius */
  modal: 3,
} as const;

/** Elevation / shadow scale. */
export const elevation = {
  soft: "0 6px 18px rgba(0,0,0,0.07)",
  card: "0 6px 20px rgba(0,0,0,0.08)",
  hover: "0 12px 32px rgba(0,0,0,0.12)",
  chip: "0 2px 6px rgba(0,0,0,0.04)",
  floating: "0 12px 40px rgba(0,0,0,0.16)",
} as const;

/** Brand colors (semantic, mode-invariant). */
export const brandColors = {
  success: "#17c964",
  warning: "#f5a524",
  error: "#f31260",
} as const;

/**
 * Platform-semantic discount / offer chip — Rich Premium Gold.
 * Distinct from primary CTA orange and from tenant Theme Color so ADD
 * remains the strongest action and tenants share one discount treatment.
 */
export const promoOfferColors = {
  bg: "#FFE9B3",
  border: "#d3b14d",
  icon: "#B8860B",
  text: "#725407",
  /** Soft gold-family depth — lighter than a CTA shadow. */
  shadow: "0 1px 2px rgba(138, 101, 8, 0.12)",
  hoverShadow: "0 2px 6px rgba(138, 101, 8, 0.16)",
} as const;

/**
 * Homepage hero "ENDS SOON" urgency badge — warm gold gradient chip on media.
 */
export const heroOfferBadgeColors = {
  /** Soft champagne chip so premium red type stays readable */
  gradientStart: "#FFF6E4",
  gradientEnd: "#F7E0B0",
  border: "#EFD7A8",
  shadow: "0 2px 8px rgba(120, 72, 0, 0.08)",
  /** Deep premium red — high contrast on soft gold chip */
  title: "#9B1B1B",
  /** Near-black subtitle — strong enough to read, secondary to title */
  subtitle: "#2A2A2A",
  highlight: "rgba(255, 255, 255, 0.55)",
} as const;

/**
 * Muted semantic accents for inline metadata icons only (labels stay neutral).
 * Tuned ~70–80% saturation so dish photography / price / ADD stay dominant.
 */
export const metadataIconColors = {
  spicy: "#C45C38",
  gluten: "#B8872E",
  soy: "#4A9A52",
  dairy: "#6B8FD4",
  nuts: "#8D6E63",
  seafood: "#3A8FB8",
  egg: "#C4A35A",
  vegetarian: "#3D8B42",
  vegan: "#3A8F5F",
  mild: "#5A9A62",
} as const;

export type MetadataIconAccent = keyof typeof metadataIconColors;

/**
 * Classic Amber brand hue — browser chrome, selection, footer wash, tenant fallback.
 * Not the accessible primary-action fill (see `PLATFORM_CTA_FILL`).
 */
export const PLATFORM_BRAND_AMBER = "#E8750C";

/**
 * Accessible Classic Amber primary-action fill for white CTA text (WCAG AA 4.5:1).
 * Distinct from `PLATFORM_BRAND_AMBER` so brand chrome can stay RasoiX amber.
 */
export const PLATFORM_CTA_FILL = "#C2410C";
export const PLATFORM_CTA_FILL_HOVER = "#AA390A";
export const PLATFORM_CTA_FILL_ACTIVE = "#9A3412";
export const PLATFORM_CTA_GRADIENT_END = PLATFORM_CTA_FILL_ACTIVE;
export const PLATFORM_CTA_TEXT = "#FFFFFF";

/** Premium Blue solid CTA already meets AA vs white; gradient start is aligned to it. */
export const PLATFORM_CTA_FILL_BLUE = "#2563EB";
export const PLATFORM_CTA_FILL_HOVER_BLUE = "#2057CE";
export const PLATFORM_CTA_FILL_ACTIVE_BLUE = "#1D4ED8";
export const PLATFORM_CTA_GRADIENT_END_BLUE = PLATFORM_CTA_FILL_ACTIVE_BLUE;

/**
 * Theme variants × color mode.
 * `classic-amber` preserves the legacy amber palette; `blue` is Premium Blue.
 */
export const themes = {
  "classic-amber": {
    light: {
      primary: "#2F2F2F",
      accent: PLATFORM_BRAND_AMBER,
      background: "#FFFFFF",
      card: "#FFFFFF",
      mutedText: "#6B7280",
      border: "rgba(0,0,0,0)",
      cta: {
        fill: PLATFORM_CTA_FILL,
        fillHover: PLATFORM_CTA_FILL_HOVER,
        fillActive: PLATFORM_CTA_FILL_ACTIVE,
        text: PLATFORM_CTA_TEXT,
        gradientStart: PLATFORM_CTA_FILL,
        gradientEnd: PLATFORM_CTA_GRADIENT_END,
        shadow: "0 10px 30px rgba(194,65,12,0.35)",
      },
    },
    dark: {
      primary: "#F8FAFC",
      accent: "#F59E0B",
      background: "#0F172A",
      card: "#1E293B",
      mutedText: "#9CA3AF",
      border: "rgba(0,0,0,0)",
      cta: {
        fill: PLATFORM_CTA_FILL,
        fillHover: PLATFORM_CTA_FILL_HOVER,
        fillActive: PLATFORM_CTA_FILL_ACTIVE,
        text: PLATFORM_CTA_TEXT,
        gradientStart: PLATFORM_CTA_FILL,
        gradientEnd: PLATFORM_CTA_GRADIENT_END,
        shadow: "0 10px 30px rgba(0,0,0,0.45)",
      },
    },
  },
  blue: {
    light: {
      primary: "#0F172A",
      accent: PLATFORM_CTA_FILL_BLUE,
      background: "#F8FAFC",
      card: "#FFFFFF",
      mutedText: "#64748B",
      border: "rgba(15,23,42,0.08)",
      cta: {
        fill: PLATFORM_CTA_FILL_BLUE,
        fillHover: PLATFORM_CTA_FILL_HOVER_BLUE,
        fillActive: PLATFORM_CTA_FILL_ACTIVE_BLUE,
        text: PLATFORM_CTA_TEXT,
        gradientStart: PLATFORM_CTA_FILL_BLUE,
        gradientEnd: PLATFORM_CTA_GRADIENT_END_BLUE,
        shadow: "0 10px 30px rgba(37,99,235,0.30)",
      },
    },
    dark: {
      primary: "#F8FAFC",
      accent: "#60A5FA",
      background: "#0B1220",
      card: "#111827",
      mutedText: "#94A3B8",
      border: "rgba(255,255,255,0.06)",
      cta: {
        fill: PLATFORM_CTA_FILL_BLUE,
        fillHover: PLATFORM_CTA_FILL_HOVER_BLUE,
        fillActive: PLATFORM_CTA_FILL_ACTIVE_BLUE,
        text: PLATFORM_CTA_TEXT,
        gradientStart: PLATFORM_CTA_FILL_BLUE,
        gradientEnd: PLATFORM_CTA_GRADIENT_END_BLUE,
        shadow: "0 12px 36px rgba(37,99,235,0.35)",
      },
    },
  },
} as const;

export type ThemeColorMode = keyof (typeof themes)["classic-amber"];

export type ThemeModeColors = (typeof themes)[ThemeVariant][ThemeColorMode];

const FALLBACK_VARIANT: ThemeVariant = "classic-amber";

/**
 * Safe token lookup — never throws on unknown variant/mode.
 * Invalid keys fall back to classic-amber / light.
 */
export function getThemeMode(
  themeVariant: ThemeVariant | string,
  mode: ThemeColorMode | string,
): ThemeModeColors {
  const variantEntry =
    (themes as Record<string, (typeof themes)[ThemeVariant] | undefined>)[themeVariant] ??
    themes[FALLBACK_VARIANT];
  const palette =
    (variantEntry as Record<string, ThemeModeColors | undefined>)[mode] ??
    variantEntry.light ??
    themes[FALLBACK_VARIANT].light;
  return palette;
}

/**
 * Legacy export: original `modeColors` === classic-amber palette only.
 * Prefer `themes["classic-amber"]` or `getThemeMode(THEME_VARIANT.AMBER, mode)` in new code.
 */
export const modeColors = themes["classic-amber"];

export function createGradients(colors: ThemeModeColors) {
  return {
    primary: `linear-gradient(135deg, ${colors.cta.gradientStart}, ${colors.cta.gradientEnd})`,
    softOverlay: (color: string) =>
      `linear-gradient(180deg, ${color}15 0%, transparent 100%)`,
  } as const;
}

const amberLightGradients = createGradients(themes["classic-amber"].light);
const amberDarkGradients = createGradients(themes["classic-amber"].dark);

/** Static amber gradients (legacy default for imports that are not theme-bound). */
export const gradients = {
  ...amberLightGradients,
  primaryDark: amberDarkGradients.primary,
} as const;

/** Motion / transition timing. */
export const motion = {
  fast: "120ms ease",
  normal: "200ms ease",
  slow: "320ms cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

/** Neutral grays for borders, overlays. */
export const neutral = {
  white: "#FFFFFF",
  black: "#000000",
  overlayLight: "rgba(0,0,0,0.04)",
  overlayMedium: "rgba(0,0,0,0.08)",
} as const;
